import { useEffect, useState, useCallback } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import CallFAB from "@/components/CallFAB";
import ChatBot from "@/components/ChatBot";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Pause, Play, XCircle, Package, Calendar, Truck, CheckCircle2, Clock, Copy, Gift,
  Wallet, Share2, MessageCircle, Phone, Edit3, Milk,
} from "lucide-react";

const WA_URL = "https://wa.me/919819933676?text=Namaste!%20Mujhe%20Divya%20Dhara%20ka%201%20din%20ka%20%E2%82%B984%20trial%20book%20karna%20hai.%20Please%20contact%20karein.%20%F0%9F%A5%9B";

export default function CustomerDashboard() {
  const { user, refreshMe } = useAuth();
  const [subs, setSubs] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [referrals, setReferrals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, d, r] = await Promise.all([
        api.get("/subscriptions/my"),
        api.get("/deliveries/my"),
        api.get("/referrals/my").catch(() => ({ data: null })),
      ]);
      setSubs(s.data);
      setDeliveries(d.data);
      setReferrals(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); refreshMe(); /* eslint-disable-next-line */ }, []);

  const updateStatus = async (sub_id, status) => {
    await api.patch(`/subscriptions/${sub_id}/status`, { status });
    load();
  };

  const code = referrals?.referral_code || user?.referral_code || "";
  const walletBalance = referrals?.wallet_balance ?? user?.wallet_balance ?? 0;
  const referralUrl = code ? `${window.location.origin}/register?ref=${encodeURIComponent(code)}` : "";

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1600); }
    catch (err) { console.warn("Clipboard copy failed:", err); }
  };
  const whatsappShare = () => {
    const msg = `Namaste! 🌿 I've been drinking pure A2 Gir cow milk from *Divya Dhara* — try it!\n\nUse my referral code *${code}* and we both get ₹100 wallet credit.\n\nSign up: ${referralUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  // Today's delivery status (first upcoming or most recent today)
  const today = new Date().toISOString().slice(0, 10);
  const todayDelivery = deliveries.find((d) => d.date === today) || deliveries[0];

  return (
    <div className="bg-[#FAF7F2] min-h-screen" data-testid="customer-dashboard">
      <Navbar />

      {/* Warm welcome header on cream background */}
      <div className="pt-24 pb-12 bg-gradient-to-br from-dd-gold-soft/50 via-[#FAF7F2] to-white border-b border-dd-line">
        <div className="dd-container">
          <div className="flex items-center gap-4">
            <img
              src="/hero-lota.png"
              alt="Divya Dhara brass lota"
              className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-2 border-dd-gold shadow-lg bg-dd-green-deep"
              data-testid="dashboard-lota-avatar"
            />
            <div>
              <div className="dd-label text-[#8F6E28] mb-1">Welcome back 🙏</div>
              <h1 className="font-serif text-3xl md:text-4xl text-dd-ink">Namaste, {user?.name?.split(" ")[0] || "Friend"}</h1>
              <p className="text-dd-ink-muted mt-1 text-sm md:text-base">Manage your A2 Gir milk delivery easily.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="dd-container py-8 md:py-10 pb-32">
        {/* Quick Actions — BIG buttons, top of page */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8" data-testid="quick-actions">
          <a href={WA_URL} target="_blank" rel="noreferrer"
             className="flex flex-col items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1faa52] text-white p-5 rounded-2xl shadow-md transition-all hover:-translate-y-0.5"
             data-testid="quick-whatsapp">
            <MessageCircle className="w-7 h-7" strokeWidth={2} />
            <span className="font-semibold text-sm">Book on WhatsApp</span>
          </a>
          <a href="tel:+919819933676"
             className="flex flex-col items-center justify-center gap-2 bg-white border-2 border-dd-gold/40 hover:border-dd-gold text-dd-ink p-5 rounded-2xl shadow-md transition-all hover:-translate-y-0.5"
             data-testid="quick-call">
            <Phone className="w-7 h-7 text-[#8F6E28]" strokeWidth={2} />
            <span className="font-semibold text-sm">Call Now</span>
          </a>
          <button onClick={whatsappShare} disabled={!code}
             className="flex flex-col items-center justify-center gap-2 bg-white border-2 border-dd-gold/40 hover:border-dd-gold text-dd-ink p-5 rounded-2xl shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-60"
             data-testid="quick-share">
            <Share2 className="w-7 h-7 text-[#8F6E28]" strokeWidth={2} />
            <span className="font-semibold text-sm">Refer & Earn ₹100</span>
          </button>
          <a href="#history"
             className="flex flex-col items-center justify-center gap-2 bg-white border-2 border-dd-gold/40 hover:border-dd-gold text-dd-ink p-5 rounded-2xl shadow-md transition-all hover:-translate-y-0.5"
             data-testid="quick-history">
            <Clock className="w-7 h-7 text-[#8F6E28]" strokeWidth={2} />
            <span className="font-semibold text-sm">Order History</span>
          </a>
        </section>

        {/* Active Subscription — hero card */}
        <section className="mb-8" data-testid="active-subscription">
          <h2 className="font-serif text-2xl text-dd-ink mb-3 flex items-center gap-2">
            <Package className="w-5 h-5 text-[#8F6E28]" /> Your Active Subscription
          </h2>
          {loading ? (
            <div className="bg-white border border-dd-line rounded-2xl p-8 text-center text-dd-ink-muted">Loading…</div>
          ) : subs.length === 0 ? (
            <div className="bg-white border border-dd-line rounded-2xl p-10 text-center">
              <Milk className="w-12 h-12 mx-auto text-[#8F6E28]/40 mb-3" strokeWidth={1.25} />
              <p className="text-dd-ink-muted mb-4">No active subscription yet.</p>
              <a href={WA_URL} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-2 bg-gradient-to-b from-dd-gold to-[#8F6E28] text-dd-ink-dark px-6 py-3 rounded-full font-bold">
                <MessageCircle className="w-4 h-4" /> Book Your First Delivery
              </a>
            </div>
          ) : (
            <div className="space-y-4">
              {subs.map((s) => (
                <div key={s.id} className="bg-white border border-dd-line rounded-2xl p-6 shadow-md" data-testid={`sub-card-${s.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <div className="font-serif text-2xl text-dd-ink">{s.plan_name}</div>
                      <div className="flex gap-4 mt-3 text-sm flex-wrap">
                        <div><span className="text-dd-ink-muted">Product:</span> <span className="font-semibold text-dd-ink">A2 Gir Cow Milk</span></div>
                        <div><span className="text-dd-ink-muted">Price:</span> <span className="font-semibold text-dd-ink">₹{s.price}</span></div>
                        <div><span className="text-dd-ink-muted">Starts:</span> <span className="font-semibold text-dd-ink">{s.start_date}</span></div>
                      </div>
                      <div className="text-xs text-dd-ink-muted mt-2">📍 {s.address}, {s.pincode}</div>
                    </div>
                    <span className={`text-[0.65rem] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full font-bold ${
                      s.status === "active" ? "bg-green-100 text-green-700" :
                      s.status === "paused" ? "bg-amber-100 text-amber-700" :
                      "bg-red-100 text-red-700"
                    }`}>{s.status}</span>
                  </div>

                  <div className="mt-5 flex gap-2 flex-wrap">
                    {s.status === "active" && (
                      <button onClick={() => updateStatus(s.id, "paused")}
                         className="flex items-center gap-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 px-4 py-2.5 rounded-full text-sm font-semibold"
                         data-testid={`sub-pause-${s.id}`}>
                        <Pause className="w-4 h-4" /> Pause Delivery
                      </button>
                    )}
                    {s.status === "paused" && (
                      <button onClick={() => updateStatus(s.id, "active")}
                         className="flex items-center gap-1.5 bg-green-50 hover:bg-green-100 text-green-800 border border-green-200 px-4 py-2.5 rounded-full text-sm font-semibold"
                         data-testid={`sub-resume-${s.id}`}>
                        <Play className="w-4 h-4" /> Resume
                      </button>
                    )}
                    <a href={WA_URL} target="_blank" rel="noreferrer"
                       className="flex items-center gap-1.5 bg-white hover:bg-dd-gold-soft border border-dd-line hover:border-dd-gold text-dd-ink px-4 py-2.5 rounded-full text-sm font-semibold"
                       data-testid={`sub-modify-${s.id}`}>
                      <Edit3 className="w-4 h-4" /> Modify Quantity
                    </a>
                    {s.status !== "cancelled" && (
                      <button onClick={() => { if (window.confirm("Cancel this subscription?")) updateStatus(s.id, "cancelled"); }}
                         className="flex items-center gap-1.5 bg-white hover:bg-red-50 border border-red-200 text-red-600 px-4 py-2.5 rounded-full text-sm font-semibold"
                         data-testid={`sub-cancel-${s.id}`}>
                        <XCircle className="w-4 h-4" /> Cancel
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Today's Delivery + Wallet — two compact cards */}
        <section className="grid md:grid-cols-2 gap-4 mb-8">
          <div className="bg-white border border-dd-line rounded-2xl p-5 shadow-md" data-testid="today-delivery-card">
            <div className="flex items-center gap-2 text-[#8F6E28]">
              <Truck className="w-4 h-4" strokeWidth={1.75} />
              <span className="dd-label">Today's Delivery</span>
            </div>
            {todayDelivery ? (
              <>
                <div className="mt-2 font-serif text-2xl text-dd-ink">
                  {todayDelivery.status === "delivered" ? "✅ Delivered" :
                   todayDelivery.status === "out_for_delivery" ? "🚚 Out for Delivery" :
                   todayDelivery.status === "scheduled" ? "⏰ Scheduled" :
                   "❌ Missed"}
                </div>
                <div className="text-xs text-dd-ink-muted mt-1">Date: {todayDelivery.date}</div>
              </>
            ) : (
              <div className="mt-2 text-dd-ink-muted text-sm">No delivery scheduled for today.</div>
            )}
          </div>

          <div className="bg-gradient-to-br from-[#14351F] to-[#0A1F15] border border-dd-gold/40 rounded-2xl p-5 shadow-md text-dd-cream" data-testid="wallet-card">
            <div className="flex items-center gap-2 text-dd-gold">
              <Wallet className="w-4 h-4" strokeWidth={1.75} />
              <span className="dd-label">Wallet Balance</span>
            </div>
            <div className="mt-2 font-serif text-4xl dd-shimmer inline-block" data-testid="wallet-balance">₹{walletBalance?.toLocaleString("en-IN") || 0}</div>
            <div className="text-xs text-dd-cream-muted mt-1">Auto-adjusted in your next billing</div>
          </div>
        </section>

        {/* Refer & Earn — emotional gold card */}
        <section className="mb-8 bg-gradient-to-br from-dd-gold-soft via-white to-dd-gold-soft/50 border-2 border-dd-gold rounded-2xl p-6 shadow-lg relative overflow-hidden" data-testid="referral-card">
          <div className="absolute -top-6 -right-6 w-24 h-24 opacity-30 pointer-events-none">
            <Gift className="w-full h-full text-[#8F6E28]" strokeWidth={0.75} />
          </div>
          <div className="flex items-center gap-2 text-[#8F6E28] mb-2">
            <Gift className="w-5 h-5" strokeWidth={1.75} />
            <span className="dd-label">Refer Friends · ₹100 for Both</span>
          </div>
          <h3 className="font-serif text-2xl text-dd-ink">Share the love of pure milk</h3>
          <p className="text-sm text-dd-ink-muted mt-1">When a friend signs up with your code, you both get ₹100 wallet credit instantly.</p>

          {code ? (
            <div className="mt-4 flex gap-2 flex-wrap">
              <div className="flex-1 min-w-[200px] flex items-center gap-2 bg-white border-2 border-dd-gold rounded-full px-4 py-2.5 shadow-sm">
                <code className="font-mono text-[#8F6E28] font-bold tracking-wide flex-1" data-testid="referral-code">{code}</code>
                <button onClick={() => copy(code)} className="text-[#8F6E28] hover:text-dd-gold text-xs font-semibold flex items-center gap-1" data-testid="copy-code-btn">
                  <Copy className="w-3.5 h-3.5" /> {copied ? "Copied!" : "Copy"}
                </button>
              </div>
              <button onClick={whatsappShare}
                 className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1faa52] text-white px-5 py-2.5 rounded-full font-bold text-sm shadow-lg"
                 data-testid="whatsapp-share-btn">
                <Share2 className="w-4 h-4" /> Share on WhatsApp
              </button>
            </div>
          ) : (
            <div className="mt-4 text-sm text-dd-ink-muted">Your unique code is being generated…</div>
          )}
          <div className="text-xs text-dd-ink-muted mt-3">
            Friends signed up: <span className="text-[#8F6E28] font-bold" data-testid="referrals-count">{referrals?.referrals?.length || 0}</span>
            {" · "}Total earned: <span className="text-[#8F6E28] font-bold">₹{referrals?.total_credit_earned || 0}</span>
          </div>
        </section>

        {/* Order History */}
        <section id="history" data-testid="order-history">
          <h2 className="font-serif text-2xl text-dd-ink mb-3 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#8F6E28]" /> Delivery History
          </h2>
          {deliveries.length === 0 ? (
            <div className="bg-white border border-dd-line rounded-2xl p-8 text-center text-dd-ink-muted">
              No deliveries yet. Start a subscription to see history here.
            </div>
          ) : (
            <div className="bg-white border border-dd-line rounded-2xl divide-y divide-dd-line overflow-hidden shadow-md">
              {deliveries.map((d) => {
                const Icon = d.status === "delivered" ? CheckCircle2 :
                             d.status === "out_for_delivery" ? Truck :
                             d.status === "missed" ? XCircle : Clock;
                const tone = d.status === "delivered" ? "bg-green-50 text-green-700" :
                             d.status === "missed" ? "bg-red-50 text-red-700" :
                             "bg-amber-50 text-amber-700";
                return (
                  <div key={d.id} className="p-4 flex items-center gap-3" data-testid={`delivery-row-${d.id}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tone}`}>
                      <Icon className="w-5 h-5" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-dd-ink">{d.date}</div>
                      <div className="text-xs text-dd-ink-muted capitalize">{d.status.replace(/_/g, " ")}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <Footer />
      <WhatsAppFAB name={user?.name} />
      <CallFAB />
      <ChatBot />
    </div>
  );
}
